import { z } from 'zod';

// Enhanced frontend validation schemas
export const LoginFormSchema = z.object({
	email: z.string()
		.min(1, 'Email không được để trống')
		.email('Email không hợp lệ')
		.max(255, 'Email quá dài'),
	password: z.string()
		.min(1, 'Mật khẩu không được để trống')
		.min(6, 'Mật khẩu phải có ít nhất 6 ký tự')
		.max(100, 'Mật khẩu quá dài'),
	rememberMe: z.boolean().default(false)
}).strip();

export const FacultyFormSchema = z.object({
	maKhoa: z.string()
		.min(1, 'Mã khoa không được để trống')
		.max(10, 'Mã khoa tối đa 10 ký tự')
		.regex(/^[A-Z0-9]+$/, 'Mã khoa chỉ được chứa chữ in hoa và số'),
	tenKhoa: z.string()
		.min(1, 'Tên khoa không được để trống')
		.max(200, 'Tên khoa tối đa 200 ký tự')
}).strip();

export const MajorFormSchema = z.object({
	maNganh: z.string()
		.min(1, 'Mã ngành không được để trống')
		.max(10, 'Mã ngành tối đa 10 ký tự')
		.regex(/^[A-Z0-9]+$/, 'Mã ngành chỉ được chứa chữ in hoa và số'),
	tenNganh: z.string()
		.min(1, 'Tên ngành không được để trống')
		.max(200, 'Tên ngành tối đa 200 ký tự'),
	maKhoa: z.string()
		.min(1, 'Phải chọn khoa')
		.max(10, 'Mã khoa tối đa 10 ký tự')
}).strip();

export const SearchFormSchema = z.object({
	query: z.string().optional(),
	faculty: z.string().optional(),
	major: z.string().optional(),
	province: z.string().optional(),
	admissionYear: z.number().int().min(1995).max(new Date().getFullYear() + 1).optional(),
	status: z.enum(['STUDYING', 'GRADUATED', 'SUSPENDED', 'TRANSFERRED']).optional(),
	gender: z.enum(['M', 'F']).optional()
}).strip();

export const FileUploadFormSchema = z.object({
	file: z.any()
		.refine((file) => file instanceof File, 'Phải chọn file')
		.refine((file) => {
			if (!(file instanceof File)) return false;
			const allowedTypes = [
				'application/vnd.ms-excel',
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				'text/csv'
			];
			return allowedTypes.includes(file.type);
		}, 'Chỉ chấp nhận file Excel (.xlsx) hoặc CSV')
		.refine((file) => {
			if (!(file instanceof File)) return false;
			return file.size <= 10 * 1024 * 1024; // 10MB
		}, 'File không được vượt quá 10MB')
}).strip();

export const AdmissionMethodFormSchema = z.object({
	maPhuongThuc: z.string()
		.min(1, 'Mã phương thức không được để trống'),
	tenPhuongThuc: z.string()
		.min(1, 'Tên phương thức không được để trống'),
	doUuTien: z.number()
		.int('Độ ưu tiên phải là số nguyên')
		.min(0, 'Độ ưu tiên phải >= 0'),
}).strip();

export const StatisticsFormSchema = z.object({
	year: z.number()
		.int('Năm phải là số nguyên')
		.min(1995, 'Năm phải từ 1995 trở lên')
		.max(new Date().getFullYear() + 1, 'Năm không được vượt quá năm hiện tại')
}).strip();

// Export types for easier usage
export type LoginFormData = z.infer<typeof LoginFormSchema>;
export type FacultyFormData = z.infer<typeof FacultyFormSchema>;
export type MajorFormData = z.infer<typeof MajorFormSchema>;
export type AdmissionMethodFormData = z.infer<typeof AdmissionMethodFormSchema>;
export type SearchFormData = z.infer<typeof SearchFormSchema>;
export type FileUploadFormData = z.infer<typeof FileUploadFormSchema>;
export type StatisticsFormData = z.infer<typeof StatisticsFormSchema>;

// Validation helpers
export const validateForm = <T>(schema: z.ZodSchema<T>, data: unknown): {
	success: boolean;
	data?: T;
	errors?: z.ZodError
} => {
	try {
		const result = schema.parse(data);
		return { success: true, data: result };
	} catch (error) {
		if (error instanceof z.ZodError) {
			return { success: false, errors: error };
		}
		throw error;
	}
};

export const getFieldError = (errors: z.ZodError | null | undefined, fieldName: string): string | undefined => {
	if (!errors) return undefined;
	const fieldError = errors.errors.find(error => error.path.includes(fieldName));
	return fieldError?.message;
};